import {
  absoluteValue,
  createBigDecimal,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { bitbankAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v1/user/spot/trade_history',
    query: {
      count: config.limit.toString(),
      ...(from ? { since: from.toString() } : {}),
      ...(to ? { end: to.toString() } : {}),
      order: 'asc',
    },
    responseSchema,
  })

  // 昇順 sort by executed_at
  const sortedTrades = response.data.trades.sort(
    (a, b) => a.executed_at - b.executed_at,
  )

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // Group trades by order_id and executed_at
  const tradeGroups = new Map<string, typeof sortedTrades>()

  for (const trade of sortedTrades) {
    // Check for unsupported interest
    if (trade.interest && trade.interest.value !== 0n) {
      throw new Error('Interest is not supported')
    }

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined
        ? trade.executed_at
        : Math.min(fetchedFrom, trade.executed_at)
    fetchedTo =
      fetchedTo === undefined
        ? trade.executed_at
        : Math.max(fetchedTo, trade.executed_at)

    const key = `${trade.order_id}_${trade.executed_at}`
    const group = tradeGroups.get(key) || []
    group.push(trade)
    tradeGroups.set(key, group)
  }

  // Process each trade group
  for (const trades of tradeGroups.values()) {
    if (trades.length === 0) continue

    const [base, quote] = trades[0].pair.split('_')

    // Position side undefined (spot trading)
    if (trades[0].position_side === undefined) {
      // Calculate total amounts
      let totalBaseAmount = trades[0].amount
      let totalBaseFee = trades[0].fee_amount_base
      let totalQuoteValue = times(trades[0].amount, trades[0].price)
      let totalQuoteFee = trades[0].fee_amount_quote

      for (let i = 1; i < trades.length; i++) {
        totalBaseAmount = plus(totalBaseAmount, trades[i].amount)
        totalBaseFee = plus(totalBaseFee, trades[i].fee_amount_base)
        totalQuoteValue = plus(
          totalQuoteValue,
          times(trades[i].amount, trades[i].price),
        )
        totalQuoteFee = plus(totalQuoteFee, trades[i].fee_amount_quote)
      }

      const baseAsset = getAsset(base)
      if (!baseAsset) {
        throw new AssetNotFoundException(base)
      }

      const quoteAsset = getAsset(quote)
      if (!quoteAsset) {
        throw new AssetNotFoundException(quote)
      }

      const netBaseAmount = minus(totalBaseAmount, totalBaseFee)
      const netQuoteAmount = minus(totalQuoteValue, totalQuoteFee)

      actions.push({
        comment: `Trade ID: ${trades.map((t) => t.trade_id).join(' / ')}`,
        evidence: 'system-rule',
        order: 0,
        source: `${bitbankAccountCategory}_${config.type}_${trades[0].order_id}_${trades[0].executed_at}`,
        type: 'trade',
        timestamp: trades[0].executed_at,
        transfers: [
          {
            asset: baseAsset,
            amount: netBaseAmount,
            direction: trades[0].side === 'buy' ? 'in' : 'out',
          },
          {
            asset: quoteAsset,
            amount: netQuoteAmount,
            direction: trades[0].side === 'buy' ? 'out' : 'in',
          },
        ],
      })
    } else {
      // Position side defined (margin trading)
      let totalProfitLoss = trades[0].profit_loss ?? createBigDecimal(0n)

      for (let i = 1; i < trades.length; i++) {
        const pl = trades[i].profit_loss ?? createBigDecimal(0n)
        totalProfitLoss = plus(totalProfitLoss, pl)
      }

      // Skip if profit/loss is zero
      if (totalProfitLoss.value === 0n) {
        continue
      }

      const quoteAsset = getAsset(quote)
      if (!quoteAsset) {
        throw new AssetNotFoundException(quote)
      }

      const isProfit = totalProfitLoss.value > 0n

      actions.push({
        comment: `Trade ID: ${trades.map((t) => t.trade_id).join(' / ')}`,
        evidence: 'system-rule',
        order: 0,
        source: `${bitbankAccountCategory}_${config.type}_${trades[0].order_id}_${trades[0].executed_at}`,
        type: isProfit ? 'realize-profit' : 'realize-loss',
        timestamp: trades[0].executed_at,
        transfers: [
          {
            asset: quoteAsset,
            amount: absoluteValue(totalProfitLoss),
            direction: isProfit ? 'in' : 'out',
          },
        ],
      })
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: response.data.trades.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
